import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Badge, Modal } from "react-bootstrap";

import HttpManager from "../../../../services/HttpManager";
import { RootState } from "../../../../globals";
import { PERMISSIONS_CODES, paymentStatus } from "../../../../constants";
import { errorText } from "../../../../navigation/v2/managment/enrollmentRequests/helpers";
import useEsTelefono from "../../../../utils/esTelefono";
import S from "./strings";
import { abrirArchivo } from "../../../enrollment/visorArchivo";

export interface ReceiptDetailModalProps {
	campusId: number;
	receiptId: number;
	onClose: () => void;
	/** Después de anular el pago completo, para que la lista se refresque. */
	onCancelled?: () => void;
	/** Abrir el detalle de un movimiento (el pago de un alumno). */
	onOpenMovement?: (paymentId: number) => void;
}

const money = (n: number, symbol: string) => symbol + Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** El PAGO como lo ve el banco, con sus movimientos por alumno debajo. */
const ReceiptDetailModal: React.FC<ReceiptDetailModalProps> = ({ campusId, receiptId, onClose, onCancelled, onOpenMovement }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const [angosto, setAngosto] = React.useState<boolean>(typeof window !== "undefined" && window.innerWidth <= 600);
	React.useEffect(() => {
		const f = () => setAngosto(window.innerWidth <= 600);
		window.addEventListener("resize", f);
		return () => window.removeEventListener("resize", f);
	}, []);
	const permissions: any[] = useSelector((state: RootState) => state.rootReducer.sessionState.permissions);
	const [r, setR] = useState<any>(null);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [asking, setAsking] = useState<boolean>(false);
	const [reason, setReason] = useState<string>("");

	const load = () => http.paymentService.getReceiptDetails(campusId, receiptId).then((res) => setR(res.data)).catch((e) => setError(errorText(e)));
	useEffect(() => { load(); }, [campusId, receiptId]);

	// El recibo se ve en el visor de WAPP, encima de la pantalla (nunca otra ventana ni descarga a ciegas).
	const pdf = () => abrirArchivo({ nombre: `Recibo del pago ${receiptId}.pdf`,
		cargar: () => http.fileService.getReceiptPDF(campusId, receiptId).then((res: any) => new Blob([res.data], { type: "application/pdf" })) })
		.catch((e) => setError(errorText(e)));
	const cancelWhole = async () => {
		if (reason.trim().length < 5) return;
		try {
			setBusy(true);
			setError(null);
			await http.paymentService.cancelReceipt(campusId, receiptId, reason.trim());
			setAsking(false);
			setNotice(S.receipt.cancelled);
			await load();
			if (onCancelled) onCancelled();
		} catch (e) {
			setError(errorText(e));
		} finally {
			setBusy(false);
		}
	};

	const cancelled = r && r.statusId === paymentStatus.CANCELLED;
	const symbol = r ? r.currencyCode : "";
	const Dato = ({ l, v }: { l: string; v: React.ReactNode }) => (
		<div style={{ minWidth: movil ? "45%" : 150 }}>
			<div style={{ fontSize: 11, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--wa-apagado)", fontWeight: 700 }}>{l}</div>
			<div style={{ fontWeight: 600 }}>{v}</div>
		</div>
	);

	return (
		<React.Fragment>
			<Modal show onHide={busy ? undefined : onClose} size="lg" centered scrollable>
				<Modal.Header closeButton={!busy}>
					<Modal.Title style={{ fontSize: movil ? 16 : 18, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
						{S.receipt.title} {receiptId}
						{r ? <Badge bg={cancelled ? "dark" : "primary"} style={{ fontWeight: 500 }}>{r.status}</Badge> : ""}
						{r && r.referenceRepeated ? <Badge bg="warning" text="dark" style={{ fontWeight: 500 }}>{S.receipt.repeatedRef}</Badge> : ""}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ textAlign: "left", fontSize: 13 }}>
					{error ? <div className="alert alert-danger" style={{ fontSize: 13 }}>{error}</div> : ""}
					{notice ? <div className="alert alert-success" style={{ fontSize: 13 }}>{notice}</div> : ""}
					{!r ? <div className="loader" /> : (
						<React.Fragment>
							<div style={{ display: "flex", flexWrap: "wrap", gap: "10px 18px" }}>
								<Dato l={S.total} v={<span style={{ fontSize: 20 }}>{money(r.total, symbol)}</span>} />
								<Dato l={S.method} v={r.paymentMethod} />
								<Dato l={S.account} v={r.recipientAccount || "-"} />
								<Dato l={S.reference} v={r.reference || "-"} />
								<Dato l={S.date} v={r.paymentDate} />
								<Dato l={S.receipt.payer} v={r.payerName ? `${r.payerName}${r.payerPhone ? " · " + r.payerPhone : ""}` : "-"} />
								<Dato l={S.receipt.registered} v={`${r.createdBy} · ${r.createdAt} · ${r.campusName}`} />
								{cancelled ? <Dato l={S.receipt.cancelledBy} v={`${r.cancelledBy} · ${r.cancelledAt}${r.cancelNotes ? " · " + r.cancelNotes : ""}`} /> : ""}
							</div>
							{r.notes ? <div style={{ marginTop: 8, color: "var(--wa-apagado)" }}>{r.notes}</div> : ""}
							{(r.audits || []).length ? <div style={{ marginTop: 8, color: "var(--wa-oro)", fontWeight: 600 }}>Auditoría: {r.audits.map((a: any) => a.details).join(" · ")}</div> : ""}

							<h6 style={{ marginTop: 16 }}>{S.receipt.movements} ({r.movementCount})</h6>
							{/* Teléfono (≤ 600 px): tarjetas apiladas; en tabla, «Ver» quedaba fuera de la pantalla. */}
							{angosto ? (
								<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
									{r.movements.map((m: any) => (
										<div key={"t" + m.id} className="waTarjeta" style={{ padding: "10px 12px", color: m.statusId === paymentStatus.CANCELLED ? "var(--wa-apagado)" : undefined,
											textDecoration: m.statusId === paymentStatus.CANCELLED ? "line-through" : undefined }}>
											<div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
												<span><b>{m.label} · {m.studentName}</b></span>
												<b style={{ whiteSpace: "nowrap" }}>{money(m.amount, symbol)}</b>
											</div>
											<div style={{ fontSize: 12, color: "var(--wa-apagado)" }}>{[m.identityCode, m.campusName, m.invoiceId ? "Factura " + m.invoiceId : "", m.status].filter(Boolean).join(" · ")}</div>
											{(m.concepts || []).map((c: any, i: number) => (
												<div key={"c" + i} style={{ fontSize: 12 }}>
													{c.title}{c.course ? <span style={{ color: "var(--wa-apagado)" }}> · {c.course}</span> : null}
													{c.applied !== null ? <span> · {money(c.applied, symbol)} {c.state === "abono" ? "(abono)" : c.state === "cero" ? "(en $0)" : ""}</span> : null}
													{c.discount ? <span style={{ color: "var(--wa-oro)" }}> · desc. {money(c.discount, symbol)}{c.promotion ? " (" + c.promotion + ")" : ""}</span> : null}
													{c.discountNote && !c.promotion ? <span style={{ color: "var(--wa-apagado)" }}> · {c.discountNote}</span> : null}
												</div>
											))}
											{onOpenMovement ? <button type="button" className="btn btn-outline-primary btn-sm rounded-pill" style={{ marginTop: 6, minHeight: 44, width: "100%" }} onClick={() => onOpenMovement(m.id)}>{S.receipt.view}</button> : null}
										</div>
									))}
								</div>
							) : (
							<div style={{ overflowX: "auto" }}>
									<table className="table table-sm" style={{ fontSize: 13, marginBottom: 0 }}>
										<thead><tr><th>#</th><th>Alumno</th><th>Plantel</th><th>Factura</th><th style={{ textAlign: "right" }}>Monto</th><th>{S.receipt.status}</th><th></th></tr></thead>
										<tbody>
											{r.movements.map((m: any) => (
												<tr key={"m" + m.id} style={{ color: m.statusId === paymentStatus.CANCELLED ? "var(--wa-apagado)" : undefined, textDecoration: m.statusId === paymentStatus.CANCELLED ? "line-through" : undefined }}>
													<td style={{ whiteSpace: "nowrap" }}>{m.label}</td>
													<td><b>{m.studentName}</b><div style={{ fontSize: 11, color: "var(--wa-apagado)" }}>{m.identityCode}</div>
														{(m.concepts || []).map((c: any, i: number) => (
															<div key={"c" + i} style={{ fontSize: 12 }}>
																{c.title}{c.course ? <span style={{ color: "var(--wa-apagado)" }}> · {c.course}</span> : null}
																{c.applied !== null ? <span> · {money(c.applied, symbol)} {c.state === "abono" ? "(abono)" : c.state === "cero" ? "(en $0)" : ""}</span> : null}
																{c.discount ? <span style={{ color: "var(--wa-oro)" }}> · desc. {money(c.discount, symbol)}{c.promotion ? " (" + c.promotion + ")" : ""}</span> : null}
																{c.discountNote && !c.promotion ? <span style={{ color: "var(--wa-apagado)" }}> · {c.discountNote}</span> : null}
															</div>
														))}
													</td>
													<td>{m.campusName}</td>
													<td>{m.invoiceId}</td>
													<td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{money(m.amount, symbol)}</td>
													<td>{m.status}</td>
													<td>{onOpenMovement ? <button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} onClick={() => onOpenMovement(m.id)}>{S.receipt.view}</button> : ""}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
							{r.documents.length > 0 ? (
								<div style={{ marginTop: 10, fontSize: 12, color: "var(--wa-apagado)" }}>{S.attachments}: {r.documents.map((d: any) => d.name).join(" · ")}</div>
							) : ""}
						</React.Fragment>
					)}
				</Modal.Body>
				<Modal.Footer style={{ flexWrap: "wrap", gap: 6 }}>
					<button type="button" className={"btn btn-outline-secondary btn-sm" + (movil ? " w-100" : "")} disabled={busy} onClick={onClose}>{S.close}</button>
					{r ? <button type="button" className={"btn btn-outline-primary btn-sm" + (movil ? " w-100" : "")} disabled={busy} onClick={pdf}>{S.downloadReceipt}</button> : ""}
					{r && !cancelled && permissions.includes(PERMISSIONS_CODES.PAYMENT_PERMISSIONS.DELETE)
						? <button type="button" className={"btn btn-warning btn-sm" + (movil ? " w-100" : "")} disabled={busy} onClick={() => { setReason(""); setAsking(true); }}>{S.receipt.cancelWhole}</button> : ""}
				</Modal.Footer>
			</Modal>

			<Modal show={asking} onHide={busy ? undefined : () => setAsking(false)} centered size="sm">
				<Modal.Header closeButton={!busy}><Modal.Title style={{ fontSize: 16 }}>{S.receipt.cancelPrompt}</Modal.Title></Modal.Header>
				<Modal.Body style={{ textAlign: "left" }}>
					<div style={{ fontSize: 12, color: "var(--wa-apagado)", marginBottom: 8 }}>{S.receipt.cancelHint}</div>
					<textarea className="form-control form-control-sm" rows={3} autoFocus value={reason} disabled={busy} placeholder={S.receipt.cancelPlaceholder} onChange={(e) => setReason(e.target.value)} />
				</Modal.Body>
				<Modal.Footer>
					<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setAsking(false)}>{S.back}</button>
					<button type="button" className="btn btn-warning btn-sm" disabled={busy || reason.trim().length < 5} onClick={cancelWhole}>{S.receipt.confirm}</button>
				</Modal.Footer>
			</Modal>
		</React.Fragment>
	);
};

export default ReceiptDetailModal;
